import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import SyncIcon from '@mui/icons-material/Sync'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { MainService, OdysseyRead, RobotRead, TaskTypeEnum } from 'api/schema'
import { Button as CustomButton } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Title from 'components/Title/Title'
import addDays from 'date-fns/addDays'
import endOfDay from 'date-fns/endOfDay'
import endOfMonth from 'date-fns/endOfMonth'
import endOfWeek from 'date-fns/endOfWeek'
import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import enUS from 'date-fns/locale/en-US'
import parse from 'date-fns/parse'
import startOfDay from 'date-fns/startOfDay'
import startOfMonth from 'date-fns/startOfMonth'
import startOfWeek from 'date-fns/startOfWeek'
import { useUser } from 'hooks/user'
import Scenario from 'pages/Scenario/Scenario'
import React from 'react'
import { useCallback, useState } from 'react'
import { useRef } from 'react'
import { Calendar, View, Views, dateFnsLocalizer } from 'react-big-calendar'
import { useTranslation } from 'react-i18next'
import robot from 'store/actions/robot'
import { PATH, TASK_STATUSES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { hasRecurringWeekdays } from 'utils/odysseyReschedule'
import { TASK_CANCEL_REASON_OPTIONS, TASK_CANCEL_REASON_OTHER } from 'utils/taskCancelReasons'
import { getVendorDisplayName } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'

import { ConfirmationDialogAllSingle } from './ConfirmationDialogAllSingle'
import { EventEditDeletePopup } from './TaskSchedulerEventEditDeletePopup'
import { exportScheduleToCSV } from './TaskSchedulerExport'
import {
  ProcessedEvent,
  addEventsStatistics,
  getColorByColorName,
  getColorById,
  getLoadingStatus,
  getTaskId,
  getTaskPrefix,
  renderMissions,
  renderScenario,
  renderStatsTable,
  scheduleToEvents,
  toISOStringYYYYMMDD,
} from './TaskSchedulerUtils'
import type { DialogTarget } from './TaskSettingsModal/DialogContext'
import TaskSettingsModal from './TaskSettingsModal/TaskSettingsModal'

import schedulerStyle from './SchedulerSettings.module.css'
import style from 'components/Button/Button.module.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

const calendarFormats = {
  timeGutterFormat: 'HH:mm',
  eventTimeRangeFormat: ({ start, end }: any, culture: any, localizer: any) =>
    localizer.format(start, 'HH:mm', culture) + ' - ' + localizer.format(end, 'HH:mm', culture),
  agendaTimeRangeFormat: ({ start, end }: any, culture: any, localizer: any) =>
    localizer.format(start, 'HH:mm', culture) + ' - ' + localizer.format(end, 'HH:mm', culture),
}

enum EventScopes {
  ALL = 'all',
  CURRENT = 'current',
}

const TaskEventViewer = ({ event }: { event: any }) => {
  const { t } = useTranslation()
  const [selectedReport, setSelectedReport] = useState<string>('Overall')

  const extendedProps = event.extendedProps
  const hasExtendedProps =
    extendedProps && extendedProps.missions_progress && extendedProps.missions_progress.length > 0

  const handleReportChange = (event: SelectChangeEvent) => {
    setSelectedReport(event.target.value as string)
  }

  const eventMissions = event.extendedProps?.missions
    ? event.extendedProps.missions
    : event.extendedProps?.mission_id
    ? [event.extendedProps.mission_id]
    : []

  let mainDescription = event.description

  if (hasExtendedProps) {
    if (selectedReport !== 'Overall') {
      mainDescription = ''
    }
  }

  const currentStats =
    hasExtendedProps &&
    (selectedReport === 'Overall'
      ? extendedProps.overall_progress
      : extendedProps.missions_progress[Number(selectedReport)])

  const currentName =
    (hasExtendedProps &&
      (selectedReport === 'Overall'
        ? extendedProps.overall_progress?.missions?.length === 1
          ? extendedProps.missions_progress[0]?.name
          : ''
        : extendedProps.missions_progress[Number(selectedReport)]?.name)) ||
    extendedProps?.mission_name

  const displayOffsetMinutes = (event as any).displayOffsetMinutes
  const taskOffsetMinutes = extendedProps?.taskOffsetMinutes

  let displayStart = event.start
  let displayEnd = event.end

  if (displayOffsetMinutes !== undefined && taskOffsetMinutes !== undefined) {
    const shiftBackMs = (displayOffsetMinutes - taskOffsetMinutes) * 60_000
    displayStart = new Date(event.start.getTime() - shiftBackMs)
    displayEnd = new Date(event.end.getTime() - shiftBackMs)
  }

  const startTime = format(displayStart, 'HH:mm')
  const endTime = format(displayEnd, 'HH:mm')
  const startDateStr = format(displayStart, 'EEEE, MMM do')
  const endDateStr = format(displayEnd, 'EEEE, MMM do')
  const dateStr = startDateStr === endDateStr ? startDateStr : `${startDateStr} - ${endDateStr}`

  // Show timezone label if the task's original timezone offset differs from the current calendar display offset
  const isTimezoneDifferent =
    displayOffsetMinutes !== undefined && taskOffsetMinutes !== undefined && displayOffsetMinutes !== taskOffsetMinutes
  const timezoneLabel = isTimezoneDifferent ? ` (${formatUtcOffset(taskOffsetMinutes).replace('UTC', '')})` : ''

  const isPast = event.start.getTime() < Date.now()
  const isExecuted = extendedProps?.status === 'executed'
  const isDeleted = extendedProps?.status === 'deleted'
  const showNoStatsMsg = isPast && !isExecuted && !isDeleted

  return (
    <div style={{ whiteSpace: 'pre-line' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--text-secondary)' }}>
          <CalendarTodayIcon sx={{ fontSize: '0.9rem', opacity: 0.8 }} />
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
            {dateStr}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--text-secondary)' }}>
          <AccessTimeIcon sx={{ fontSize: '0.9rem', opacity: 0.8 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {startTime} - {endTime}
            {timezoneLabel}
          </Typography>
        </Box>
      </Box>

      {hasExtendedProps && eventMissions.length > 1 && (
        <Box sx={{ minWidth: 120, mb: 1, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-select-label">Report</InputLabel>
            <Select
              labelId="report-select-label"
              id="report-select"
              value={selectedReport}
              label="Report"
              onChange={handleReportChange}
            >
              <MenuItem value={'Overall'}>Overall</MenuItem>
              {extendedProps.missions_progress.map((mission: any, index: number) => (
                <MenuItem key={`${mission.id}_${index}`} value={index.toString()}>
                  Mission {mission.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {showNoStatsMsg && (
        <Box sx={{ fontSize: '0.875rem', mb: 1, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
          {t('scheduler.no_statistic')}
        </Box>
      )}
      {isDeleted && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px solid var(--form-border)',
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', mb: 0.5, color: 'var(--text-secondary)', fontSize: '0.875rem' }}
          >
            {event.extendedProps.mission_status === 8
              ? 'The task was aborted'
              : event.extendedProps.mission_status === 99 || event.extendedProps.mission_status === 10
              ? 'The task was not started'
              : 'The task was canceled'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 0.0 }}>
            <span style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>Reason: </span>
            {event.extendedProps.exception_reason}
          </Typography>
          {event.extendedProps.exception_user && (
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              <span style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>
                {event.extendedProps.mission_status === 8 ? 'Aborted by: ' : 'Canceled by: '}
              </span>
              {event.extendedProps.exception_user}
            </Typography>
          )}
        </Box>
      )}
      {mainDescription && <Box sx={{ fontSize: '0.875rem', mb: 0 }}>{mainDescription}</Box>}
      {selectedReport === 'Overall' && renderMissions(eventMissions)}

      {currentStats && (
        <Box>
          {currentName && (
            <Typography variant="body2" sx={{ mb: 0.0, color: 'var(--text-main)' }}>
              <span style={{ fontWeight: 'bold' }}>Mission name:</span> {currentName}
            </Typography>
          )}
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Report:
          </Typography>
          {renderStatsTable(currentStats)}
        </Box>
      )}

      {hasExtendedProps && selectedReport !== 'Overall' && (
        <Box sx={{ mt: 1 }}>
          <a
            className={schedulerStyle.link}
            href={`${PATH.MISSION}${extendedProps.missions_progress[Number(selectedReport)]?.id}`}
            target="_blank"
            rel="noreferrer"
          >
            Go to mission
          </a>
        </Box>
      )}
    </div>
  )
}

const TriggerEdit = ({ onClose, onEdit }: { onClose: () => void; onEdit: () => void }) => {
  React.useEffect(() => {
    onEdit()
    onClose()
  }, [onClose, onEdit])
  return null
}

const buildRobotIdToFacilityIds = (facilities: { id: number; robots_ids?: number[] }[]): Map<number, number[]> => {
  const m = new Map<number, number[]>()
  for (const f of facilities) {
    for (const rid of f.robots_ids || []) {
      if (!m.has(rid)) m.set(rid, [])
      m.get(rid)!.push(f.id)
    }
  }
  return m
}

// --- Timezone display helpers ---
const formatUtcOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Whole-hour UTC offsets from -12:00 to +14:00
const UTC_OFFSET_OPTIONS = Array.from({ length: 27 }, (_, i) => {
  const minutes = (i - 12) * 60
  return { value: minutes, text: formatUtcOffset(minutes) }
})

const TaskScheduler = () => {
  const [odysseys, setOdysseys] = React.useState<OdysseyRead[]>([])
  const [robots, setRobots] = React.useState<RobotRead[]>([])
  const [eventScope, setEventScope] = React.useState<string>(EventScopes.CURRENT)
  const [refreshTaskScheduleCount, setRefreshTaskScheduleCount] = React.useState(0)
  const [calendarEvents, setCalendarEvents] = React.useState<ProcessedEvent[]>([])
  const calendarEventsRef = React.useRef<ProcessedEvent[]>([])

  const [filterFacilityId, setFilterFacilityId] = useState<string>('all')
  const [filterVendor, setFilterVendor] = useState<'all' | VendorCode>('all')
  const [filterRobot, setFilterRobot] = useState<any | null>(null)
  const [filterMission, setFilterMission] = useState<any | null>(null)
  const [filterStartAfter, setFilterStartAfter] = useState<number>(-1)
  const [displayOffsetMinutes, setDisplayOffsetMinutes] = useState<number>(() => -new Date().getTimezoneOffset())

  // Keep ref in sync with state
  React.useEffect(() => {
    calendarEventsRef.current = calendarEvents
  }, [calendarEvents])

  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date())
  const { t } = useTranslation()
  const [view, setView] = React.useState<View>(Views.MONTH)
  const [currentRange, setCurrentRange] = React.useState<{ start: Date; end: Date }>(() => {
    const now = new Date()
    return {
      start: startOfMonth(now),
      end: endOfMonth(now),
    }
  })

  const updateRange = useCallback(
    (date: Date, currentView: View) => {
      let start: Date, end: Date
      if (currentView === Views.MONTH) {
        start = startOfMonth(date)
        end = endOfMonth(date)
      } else if (currentView === Views.AGENDA) {
        start = startOfDay(date)
        end = addDays(date, 1)
      } else if (currentView === Views.WEEK) {
        start = startOfWeek(date)
        end = endOfWeek(date)
      } else {
        start = startOfDay(date)
        end = endOfDay(date)
      }

      // Only update if actually different to prevent loops
      setCurrentRange(prev => {
        if (prev.start.getTime() === start.getTime() && prev.end.getTime() === end.getTime()) {
          return prev
        }
        return { start, end }
      })
    },
    [setCurrentRange]
  )

  const handleViewChange = useCallback(
    (newView: View) => {
      setView(newView)
      updateRange(selectedDate, newView)
    },
    [selectedDate, updateRange]
  )

  const handleNavigate = useCallback(
    (newDate: Date) => {
      setSelectedDate(newDate)
      updateRange(newDate, view)
    },
    [view, updateRange]
  )
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isEditConfirmationOpen, setIsEditConfirmationOpen] = useState(false)
  const [isSingleTaskEditing, setSingleTaskEditing] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<ProcessedEvent | null>(null)
  const [openDeleteScheduleDialog, setOpenDeleteScheduleDialog] = React.useState(false)

  const getTaskById = useCallback(
    (eventId: string): OdysseyRead | undefined => {
      const prefix = getTaskPrefix(eventId)
      const taskId = getTaskId(eventId)
      if (prefix === 'odyssey') return odysseys.find((odyssey: OdysseyRead) => odyssey.id === taskId)
      console.error(`Failed to find a task. Unknown prefix ${prefix}`)
    },
    [odysseys]
  )

  const isAllValueDisabled = (selectedEvent: ProcessedEvent | null): boolean => {
    if (!selectedEvent) return true
    const task = getTaskById(selectedEvent.event_id as string)
    if (!task || !task.scheduler_settings || !task.scheduler_settings.weekdays) return true

    const weekdays = task.scheduler_settings.weekdays
    const hasWeekdays = Object.values(weekdays).some(val => val === 'true')
    return !hasWeekdays
  }
  const onSettingsCloseClicked = useCallback(
    (refresh: boolean) => {
      setIsSettingsOpen(false)
      setSelectedEvent(null)
    },
    [setIsSettingsOpen, setSelectedEvent]
  )

  const onSettingsSubmitClicked = useCallback(
    (refresh: boolean) => {
      setIsSettingsOpen(false)
      setSelectedEvent(null)
      if (refresh) {
        setRefreshTaskScheduleCount(prevKey => prevKey + 1) // Increment refreshKey to trigger re-render
      }
    },
    [setIsSettingsOpen, setSelectedEvent]
  )

  const [eventsLoaded, setEventsLoaded] = useState(false)
  const isLoadingEvents = useRef(false)
  // Cache data to avoid refetching
  const dataCache = useRef({
    robots: [] as RobotRead[],
    missionsMap: new Map<number, string>(),
    facilities: [] as { id: number; name?: string | null; robots_ids?: number[] }[],
    robotIdToFacilityIds: new Map<number, number[]>(),
    fetched: { tasks: false, odysseys: false, robots: false, missions: false, facilities: false },
    eventStatistics: [] as any[],
  })

  // Fetch initial data if not already cached
  const fetchInitialData = useCallback(async (force: boolean = false) => {
    const promises = []

    if (force || !dataCache.current.fetched.robots) {
      promises.push(
        MainService.mainRobotList(['-id'], 1, 1000).then(res => {
          dataCache.current.robots = res.results ?? []
          setRobots(dataCache.current.robots)
          setOdysseys(dataCache.current.robots.flatMap(robot => robot.odysseys))
          dataCache.current.fetched.robots = true
        })
      )
    }

    if (force || !dataCache.current.fetched.missions) {
      promises.push(
        MainService.mainMissionList(['-id'], 1, 1000).then(res => {
          const map = new Map<number, string>()
          res.results?.forEach(m => {
            if (m.name) map.set(m.id, m.name)
          })
          dataCache.current.missionsMap = map
          dataCache.current.fetched.missions = true
        })
      )
    }

    if (force || !dataCache.current.fetched.facilities) {
      promises.push(
        MainApi.fetchFacilities(1, 1000).then(res => {
          dataCache.current.facilities = res.results ?? []
          dataCache.current.robotIdToFacilityIds = buildRobotIdToFacilityIds(dataCache.current.facilities)
          dataCache.current.fetched.facilities = true
        })
      )
    }

    if (promises.length > 0) {
      await Promise.all(promises)
    }
  }, [])

  // Generate events from robot data
  const generateEventsFromRobots = useCallback(
    (start: Date, end: Date): ProcessedEvent[] => {
      const eventsSet: ProcessedEvent[] = []

      for (const robot of dataCache.current.robots) {
        const facilityIds = dataCache.current.robotIdToFacilityIds.get(robot.id) ?? []

        // Generate events from odysseys
        for (const odyssey of robot.odysseys) {
          if (odyssey.job_type === TaskTypeEnum.DELETE_SCENARIO) continue

          const odysseyEvents = scheduleToEvents(
            start,
            end,
            odyssey,
            robot.robot_color ? () => getColorByColorName(robot.robot_color || 'White') : () => getColorById(robot.id),
            <React.Fragment>{odyssey?.scenario ? renderScenario(odyssey.scenario) : null}</React.Fragment>,
            robot,
            dataCache.current.missionsMap,
            facilityIds,
            displayOffsetMinutes
          )
          // Add live status for calendar tile indicator
          odysseyEvents.forEach(e => {
            if (e.extendedProps) e.extendedProps.liveStatus = odyssey.status
          })
          eventsSet.push(...odysseyEvents)
        }
      }

      return eventsSet
    },
    [displayOffsetMinutes]
  )

  const prevRefreshCount = useRef(refreshTaskScheduleCount)
  const getRemoteEvents = React.useCallback(
    async (start: Date, end: Date) => {
      const isManualRefresh = prevRefreshCount.current !== refreshTaskScheduleCount
      prevRefreshCount.current = refreshTaskScheduleCount

      // Prevent duplicate/concurrent execution
      if (isLoadingEvents.current) {
        return []
      }

      isLoadingEvents.current = true

      try {
        // Fetch initial data once (or on manual refresh)
        await fetchInitialData(isManualRefresh)

        // Add a 7-day buffer to the fetch range to ensure visible days from adjacent months are loaded
        const fetchStart = addDays(start, -7)
        const fetchEnd = addDays(end, 7)
        // Fetch event statistics
        const [responseEventStatistics, responseOdysseyStatistics] = await Promise.all([
          MainService.getEventStatisticsByDateRange(
            format(fetchStart, "yyyy-MM-dd'T'HH:mm:ss"),
            format(fetchEnd, "yyyy-MM-dd'T'HH:mm:ss")
          ),
          MainService.getOdysseyEventStatisticsByDateRange(
            format(fetchStart, "yyyy-MM-dd'T'HH:mm:ss"),
            format(fetchEnd, "yyyy-MM-dd'T'HH:mm:ss")
          ),
        ])

        const eventStatisticsSet = [
          ...(responseEventStatistics.results ?? []),
          ...(responseOdysseyStatistics.results ?? []),
        ]
        dataCache.current.eventStatistics = eventStatisticsSet

        // Generate events from robots
        const eventsSet = generateEventsFromRobots(fetchStart, fetchEnd)

        // Add event statistics
        addEventsStatistics(
          eventsSet,
          eventStatisticsSet,
          dataCache.current.robots,
          dataCache.current.missionsMap,
          dataCache.current.robotIdToFacilityIds
        )

        setCalendarEvents(eventsSet)
        setEventsLoaded(true)
        isLoadingEvents.current = false
        return eventsSet
      } catch (error) {
        console.error('Failed to generate events', error)
        isLoadingEvents.current = false
        return []
      }
    },
    [refreshTaskScheduleCount, generateEventsFromRobots, fetchInitialData]
  )

  const lastFetchedRange = useRef<{ start: number; end: number; refreshCount: number }>({
    start: 0,
    end: 0,
    refreshCount: -1,
  })

  React.useEffect(() => {
    const s = currentRange.start.getTime()
    const e = currentRange.end.getTime()
    if (
      s !== e &&
      (s !== lastFetchedRange.current.start ||
        e !== lastFetchedRange.current.end ||
        refreshTaskScheduleCount !== lastFetchedRange.current.refreshCount)
    ) {
      lastFetchedRange.current = { start: s, end: e, refreshCount: refreshTaskScheduleCount }
      getRemoteEvents(currentRange.start, currentRange.end)
    }
  }, [currentRange, getRemoteEvents, refreshTaskScheduleCount])

  // Poll robot statuses every TIMEOUT_FETCHING_STATUS ms so task loading status stays up to date
  React.useEffect(() => {
    if (isSettingsOpen) return
    const interval = setInterval(async () => {
      try {
        const res = await MainService.mainRobotList(['-id'], 1, 1000)
        const freshRobots = res.results ?? []
        dataCache.current.robots = freshRobots
        setRobots(freshRobots)
        setOdysseys(freshRobots.flatMap(r => r.odysseys))

        // Buffer the range exactly like getRemoteEvents to preserve adjacent month visibility
        const fetchStart = addDays(currentRange.start, -7)
        const fetchEnd = addDays(currentRange.end, 7)

        // Generate fresh events from robot data
        const eventsSet = generateEventsFromRobots(fetchStart, fetchEnd)

        // Re-apply cached statistics so finished job results don't disappear on poll
        addEventsStatistics(
          eventsSet,
          dataCache.current.eventStatistics,
          dataCache.current.robots,
          dataCache.current.missionsMap,
          dataCache.current.robotIdToFacilityIds
        )

        setCalendarEvents(eventsSet)
      } catch {
        // Silently ignore transient errors
      }
    }, TIMEOUT_FETCHING_STATUS)
    return () => clearInterval(interval)
  }, [currentRange.end, currentRange.start, generateEventsFromRobots, isSettingsOpen])

  const user = useUser()
  const [deleteReason, setDeleteReason] = useState<string>('')
  const [deleteDescription, setDeleteDescription] = React.useState('')

  const handleSubmitDeleteSchedule = async (): Promise<void> => {
    try {
      if (!selectedEvent) {
        throw new Error(`The selected event is null`)
      }
      const foundTask = getTaskById(selectedEvent.event_id as string)
      if (eventScope === EventScopes.CURRENT && hasRecurringWeekdays(foundTask?.scheduler_settings?.weekdays)) {
        if (foundTask && foundTask.scheduler_settings) {
          const dateStr = toISOStringYYYYMMDD(selectedEvent.start)
          let new_scheduler_settings = {
            ...foundTask.scheduler_settings,
            except_days: [...foundTask.scheduler_settings.except_days],
            except_days_descriptions: { ...foundTask.scheduler_settings.except_days_descriptions },
            except_days_users: { ...(foundTask.scheduler_settings.except_days_users || {}) },
          }
          new_scheduler_settings.except_days.push(dateStr)
          new_scheduler_settings.except_days.sort()
          const finalDescription =
            deleteReason === TASK_CANCEL_REASON_OTHER ? deleteDescription : deleteReason
          new_scheduler_settings.except_days_descriptions[dateStr] = finalDescription

          const userName = user?.state.firstName
            ? `${user.state.firstName} ${user.state.lastName || ''}`.trim()
            : user?.state.email || 'Unknown User'
          new_scheduler_settings.except_days_users[dateStr] = userName

          await RobotApi.updateTheEntireOdyssey(
            getTaskId(selectedEvent.event_id as string),
            foundTask.missions,
            foundTask.robot_id,
            new_scheduler_settings,
            foundTask.name ? foundTask.name : 'Cleaning Task',
            foundTask.scenario,
            TASK_STATUSES.NEW
          )
        }
      } else {
        if (foundTask) {
          await RobotApi.updateOdysseyType(foundTask.id, TaskTypeEnum.DELETE_SCENARIO)
        } else {
          console.error(`There is not task with ID ${selectedEvent.event_id} to delete!`)
        }
      }
      setOpenDeleteScheduleDialog(false)
      setDeleteDescription('')
      setDeleteReason('')
      setSelectedEvent(null)
    } catch (e) {
      console.error(`Failed to delete scheduled task: ${e}`)
    }
    setRefreshTaskScheduleCount(oldValue => ++oldValue)
    setEventScope(EventScopes.CURRENT)
  }

  const theme = useTheme()

  const eventPropGetter = useCallback(
    (event: any) => ({
      style: {
        backgroundColor: event.color || '#3174ad',
        borderRadius: '0',
        opacity: theme.palette.mode === 'dark' ? 0.85 : view === Views.AGENDA ? 0.9 : 1,
        filter: theme.palette.mode === 'dark' ? 'brightness(0.9) saturate(0.8)' : 'none',
        color: 'white',
        border: '0px',
        display: 'block',
      },
    }),
    [view, theme.palette.mode]
  )

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [popoverEvent, setPopoverEvent] = useState<any | null>(null)

  const handleEventClick = (event: any, e: React.SyntheticEvent) => {
    const target = (e.currentTarget || e.target) as HTMLElement
    setAnchorEl(target)
    setPopoverEvent(event)
    setSelectedEvent(event)
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
    setPopoverEvent(null)
  }

  const filterOptions = React.useMemo(() => {
    const reachableFacilityIds = new Set<number>()
    const reachableVendors = new Set<VendorCode>()
    const reachableRobots = new Map<number, any>()
    const reachableMissions = new Map<number, any>()

    const matches = (
      event: ProcessedEvent,
      opts: {
        ignoreFacility?: boolean
        ignoreVendor?: boolean
        ignoreRobot?: boolean
        ignoreMission?: boolean
      } = {}
    ) => {
      const props = event.extendedProps || {}

      if (!opts.ignoreFacility && filterFacilityId !== 'all') {
        const fid = Number(filterFacilityId)
        const fids = (props.facility_ids as number[] | undefined) || []
        if (!fids.includes(fid)) return false
      }

      if (!opts.ignoreVendor && filterVendor !== 'all' && props.vendor !== filterVendor) return false

      if (!opts.ignoreRobot && filterRobot && props.robot_id !== filterRobot.id) return false

      if (!opts.ignoreMission && filterMission) {
        const id = filterMission.id
        const missionIds = [
          ...(props.mission_id ? [props.mission_id] : []),
          ...(props.missions || []),
          ...(props.missions_info?.map((m: any) => m.id) || []),
        ]
        if (!missionIds.includes(id)) return false
      }

      return true
    }

    calendarEvents.forEach(event => {
      const props = event.extendedProps || {}

      if (matches(event, { ignoreFacility: true }) && props.facility_ids?.length) {
        ;(props.facility_ids as number[]).forEach((fid: number) => reachableFacilityIds.add(fid))
      }

      if (matches(event, { ignoreVendor: true }) && props.vendor) {
        reachableVendors.add(props.vendor as VendorCode)
      }

      if (matches(event, { ignoreRobot: true }) && props.robot_id) {
        reachableRobots.set(props.robot_id, {
          id: props.robot_id,
          name: props.robot_name || '',
          sn: props.robot_sn || '',
        })
      }

      if (matches(event, { ignoreMission: true })) {
        if (props.mission_id && props.mission_name) {
          reachableMissions.set(props.mission_id, { id: props.mission_id, name: props.mission_name })
        }
        if (props.missions_info) {
          props.missions_info.forEach((m: any) => reachableMissions.set(m.id, { id: m.id, name: m.name }))
        }
        if (props.missions) {
          props.missions.forEach((mId: number) => {
            if (!reachableMissions.has(mId)) {
              const name = dataCache.current.missionsMap.get(mId)
              if (name) reachableMissions.set(mId, { id: mId, name })
            }
          })
        }
      }
    })

    const facilityOptions = dataCache.current.facilities
      .filter(f => reachableFacilityIds.has(f.id))
      .map(f => ({ id: f.id, name: f.name || `Facility ${f.id}` }))
      .sort((a, b) => a.name.localeCompare(b.name))

    return {
      robots: Array.from(reachableRobots.values()).sort((a, b) => a.name.localeCompare(b.name)),
      missions: Array.from(reachableMissions.values()).sort((a, b) => a.name.localeCompare(b.name)),
      facilityOptions,
      vendors: Array.from(reachableVendors).sort(),
    }
  }, [calendarEvents, filterFacilityId, filterVendor, filterRobot, filterMission])

  React.useEffect(() => {
    if (filterFacilityId !== 'all' && !filterOptions.facilityOptions.some(f => String(f.id) === filterFacilityId)) {
      setFilterFacilityId('all')
    }

    if (filterVendor !== 'all' && !filterOptions.vendors.includes(filterVendor)) {
      setFilterVendor('all')
    }

    if (filterRobot && !filterOptions.robots.find(r => r.id === filterRobot.id)) {
      setFilterRobot(null)
    }

    if (filterMission && !filterOptions.missions.find(m => m.id === filterMission.id)) {
      setFilterMission(null)
    }
  }, [filterOptions, filterFacilityId, filterVendor, filterRobot, filterMission])

  const filteredCalendarEvents = React.useMemo(() => {
    if (!calendarEvents.length) return []

    const filtered = calendarEvents.filter(event => {
      const props = event.extendedProps || {}
      const eventBrowserOffsetMinutes = -event.start.getTimezoneOffset()
      const eventShiftMs = (displayOffsetMinutes - eventBrowserOffsetMinutes) * 60_000
      const displayStart = eventShiftMs !== 0 ? new Date(event.start.getTime() + eventShiftMs) : event.start

      if (filterFacilityId !== 'all') {
        const fid = Number(filterFacilityId)
        const fids = (props.facility_ids as number[] | undefined) || []
        if (!fids.includes(fid)) return false
      }

      if (filterVendor !== 'all' && props.vendor !== filterVendor) return false

      if (filterRobot && props.robot_id !== filterRobot.id) return false

      if (filterMission) {
        const id = filterMission.id
        const missionMatches =
          props.mission_id === id || props.missions?.includes(id) || props.missions_info?.some((m: any) => m.id === id)
        if (!missionMatches) return false
      }

      if (filterStartAfter !== -1) {
        if (displayStart.getHours() < filterStartAfter) return false
      }

      return true
    })

    // Shift start/end so react-big-calendar renders events in the selected timezone
    return filtered.map(event => {
      const eventBrowserOffsetMinutes = -event.start.getTimezoneOffset()
      const eventShiftMs = (displayOffsetMinutes - eventBrowserOffsetMinutes) * 60_000
      if (eventShiftMs === 0) return { ...event, displayOffsetMinutes }
      return {
        ...event,
        start: new Date(event.start.getTime() + eventShiftMs),
        end: new Date(event.end.getTime() + eventShiftMs),
        displayOffsetMinutes: displayOffsetMinutes,
      }
    })
  }, [
    calendarEvents,
    filterFacilityId,
    filterVendor,
    filterRobot,
    filterMission,
    filterStartAfter,
    displayOffsetMinutes,
  ])
  const agendaEvents = React.useMemo(() => {
    if (view !== Views.AGENDA) return filteredCalendarEvents

    // Agenda view MUST be strictly filtered to the selected day
    const dayStart = startOfDay(selectedDate).getTime()
    const dayEnd = endOfDay(selectedDate).getTime()

    return filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= dayEnd && e >= dayStart
    })
  }, [filteredCalendarEvents, view, selectedDate])

  const hasActiveFilters = React.useMemo(() => {
    const browserOffsetMinutes = -new Date().getTimezoneOffset()
    return (
      filterFacilityId !== 'all' ||
      filterVendor !== 'all' ||
      filterRobot !== null ||
      filterMission !== null ||
      filterStartAfter !== -1 ||
      displayOffsetMinutes !== browserOffsetMinutes
    )
  }, [filterFacilityId, filterVendor, filterRobot, filterMission, filterStartAfter, displayOffsetMinutes])

  const handleClearFilters = () => {
    setFilterFacilityId('all')
    setFilterVendor('all')
    setFilterRobot(null)
    setFilterMission(null)
    setFilterStartAfter(-1)
    setDisplayOffsetMinutes(-new Date().getTimezoneOffset())
  }

  const handleExportSchedule = () => {
    // Export should strictly match the current view range (Day, Week, or Month)
    let rangeStart = startOfDay(selectedDate)
    let rangeEnd = endOfDay(selectedDate)

    if (view === Views.WEEK) {
      rangeStart = startOfWeek(selectedDate, { weekStartsOn: 1 })
      rangeEnd = endOfWeek(selectedDate, { weekStartsOn: 1 })
    } else if (view === Views.MONTH) {
      rangeStart = startOfMonth(selectedDate)
      rangeEnd = endOfMonth(selectedDate)
    }

    const start = rangeStart.getTime()
    const end = rangeEnd.getTime()

    const exportEvents = filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= end && e >= start
    })

    exportScheduleToCSV(exportEvents)
  }

  return (
    <>
      <div className={schedulerStyle.mainView}>
        <div className={schedulerStyle.titleBlock}>
          <Title text={t('header.calendar')} />
          <div style={{ display: 'flex', gap: '16px' }}>
            <CustomButton.Outlined className={style.button} style={{ width: '150px' }} onClick={handleExportSchedule}>
              Export
            </CustomButton.Outlined>
            <CustomButton.Standart
              className={style.button}
              style={{ width: '250px' }}
              onClick={() => {
                setIsSettingsOpen(true)
                setSelectedEvent(null)
              }}
            >
              New Task
            </CustomButton.Standart>
          </div>
        </div>

        <div className={schedulerStyle.filtersBlock}>
          <Dropdown
            className={schedulerStyle.filterTimezone}
            minWidth={160}
            currentOption={{
              value: displayOffsetMinutes,
              text: formatUtcOffset(displayOffsetMinutes),
            }}
            placeholder={formatUtcOffset(displayOffsetMinutes)}
            options={UTC_OFFSET_OPTIONS}
            onClickOption={(opt: IOption) => setDisplayOffsetMinutes(Number(opt.value))}
          />
          <div className={schedulerStyle.filters}>
            <Dropdown
              className={schedulerStyle.filterFacility}
              minWidth={300}
              currentOption={
                filterFacilityId !== 'all'
                  ? {
                      value: filterFacilityId,
                      text:
                        filterOptions.facilityOptions.find(f => String(f.id) === filterFacilityId)?.name ||
                        `Facility ${filterFacilityId}`,
                    }
                  : undefined
              }
              placeholder="All Facilities"
              options={[
                { value: 'all', text: 'All Facilities' },
                ...filterOptions.facilityOptions.map(f => ({ value: String(f.id), text: f.name })),
              ]}
              onClickOption={(opt: IOption) => setFilterFacilityId(String(opt.value))}
            />

            <Dropdown
              className={schedulerStyle.filterVendor}
              minWidth={300}
              currentOption={
                filterVendor !== 'all' ? { value: filterVendor, text: getVendorDisplayName(filterVendor) } : undefined
              }
              placeholder="All Vendors"
              options={[
                { value: 'all', text: 'All Vendors' },
                ...filterOptions.vendors.map(v => ({ value: v, text: getVendorDisplayName(v) })),
              ]}
              onClickOption={(opt: IOption) => setFilterVendor(opt.value as 'all' | VendorCode)}
            />

            <Dropdown
              className={schedulerStyle.filterRobot}
              minWidth={300}
              currentOption={
                filterRobot ? { value: String(filterRobot.id), text: filterRobot.name || filterRobot.sn } : undefined
              }
              placeholder="All Robots"
              options={[
                { value: '', text: 'All Robots' },
                ...filterOptions.robots.map(r => ({ value: String(r.id), text: r.name || r.sn })),
              ]}
              onClickOption={(opt: IOption) => {
                const id = Number(opt.value)
                const robot = filterOptions.robots.find(r => r.id === id) || null
                setFilterRobot(robot)
              }}
            />

            <Dropdown
              className={schedulerStyle.filterMission}
              minWidth={400}
              currentOption={filterMission ? { value: String(filterMission.id), text: filterMission.name } : undefined}
              placeholder="All Missions"
              options={[
                { value: '', text: 'All Missions' },
                ...filterOptions.missions.map(m => ({ value: String(m.id), text: m.name })),
              ]}
              onClickOption={(opt: IOption) => {
                const id = Number(opt.value)
                const mission = filterOptions.missions.find(m => m.id === id) || null
                setFilterMission(mission)
              }}
            />

            <Dropdown
              className={schedulerStyle.filterTime}
              minWidth={200}
              currentOption={
                filterStartAfter !== -1
                  ? {
                      value: String(filterStartAfter),
                      text: `Start After: ${filterStartAfter < 10 ? `0${filterStartAfter}` : filterStartAfter}:00`,
                    }
                  : undefined
              }
              placeholder="Start After: 00:00"
              options={[
                ...Array.from({ length: 24 }).map((_, i) => ({
                  value: i,
                  text: `Start After: ${i < 10 ? `0${i}` : i}:00`,
                })),
              ]}
              onClickOption={(opt: IOption) => setFilterStartAfter(Number(opt.value))}
            />

            <CustomButton.Outlined className={schedulerStyle.clearButton} onClick={handleClearFilters}>
              Clear Filters
            </CustomButton.Outlined>
          </div>
        </div>
        <div className={schedulerStyle.calendarWrapper}>
          <Calendar
            localizer={localizer}
            events={agendaEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            formats={calendarFormats}
            view={view}
            onView={handleViewChange}
            date={selectedDate}
            onNavigate={handleNavigate}
            length={view === Views.AGENDA ? 1 : undefined}
            onRangeChange={(range: any) => {
              const newStart = Array.isArray(range) ? range[0] : range.start
              const newEnd = Array.isArray(range) ? range[range.length - 1] : range.end

              if (newStart && newEnd) {
                const s = newStart.getTime()
                const e = newEnd.getTime()

                setCurrentRange(prev => {
                  if (prev.start.getTime() === s && prev.end.getTime() === e) {
                    return prev
                  }
                  return { start: new Date(s), end: new Date(e) }
                })
              }
            }}
            eventPropGetter={eventPropGetter}
            onSelectEvent={handleEventClick}
            tooltipAccessor={(e: any) => e.title}
            components={{
              event: ({ event }: { event: any }) => {
                const liveStatus = event.extendedProps?.liveStatus
                const statusMsg = getLoadingStatus(liveStatus)
                const isFuture = event.start.getTime() >= Date.now()
                const isNotLoaded = isFuture && statusMsg === 'Not Loaded'
                const isLoading = isFuture && statusMsg === 'Loading'

                return (
                  <div
                    title={typeof event.title === 'string' ? event.title : undefined}
                    style={{
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      lineHeight: '1.2',
                    }}
                  >
                    {isLoading && (
                      <SyncIcon
                        sx={{
                          fontSize: '12px',
                          color: '#fff',
                          animation: 'spin 2s linear infinite',
                          '@keyframes spin': { '100%': { transform: 'rotate(-360deg)' } },
                        }}
                      />
                    )}
                    {isNotLoaded && <ErrorRoundedIcon sx={{ fontSize: '13px', color: '#fff' }} />}
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.title}</span>
                  </div>
                )
              },
            }}
          />
        </div>

        <Popover
          open={Boolean(anchorEl) && Boolean(popoverEvent)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          disableRestoreFocus
          disableEnforceFocus
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              p: 0,
              borderRadius: 'var(--radius-lg)',
              minWidth: 350,
              maxWidth: 450,
              background: 'var(--glass-bg)',
              backdropFilter: 'var(--glass-blur)',
              WebkitBackdropFilter: 'var(--glass-blur)',
              boxShadow: 'var(--glass-shadow)',
              border: 'var(--glass-border)',
              overflow: 'hidden',
            },
          }}
        >
          {popoverEvent && (
            <Box>
              <Box sx={{ height: '6px', bgcolor: popoverEvent.color || '#3174ad' }} />
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-main)', lineHeight: 1.2 }}>
                    {popoverEvent.title}
                  </Typography>
                  <IconButton size="small" onClick={handleClosePopover} sx={{ mt: -0.5, mr: -0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Task loading status indication at top-left of the card */}
                {(() => {
                  const task = selectedEvent ? getTaskById(selectedEvent.event_id as string) : null
                  const loadingMsg = task ? getLoadingStatus(task.status) : ''
                  if (!loadingMsg || loadingMsg === 'Loaded' || popoverEvent.start.getTime() < Date.now()) return null
                  const isLoading = loadingMsg === 'Loading'
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        p: '4px 10px',
                        borderRadius: '16px',
                        width: 'fit-content',
                        bgcolor: isLoading ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                        border: isLoading ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(239,68,68,0.4)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: isLoading ? '#f59e0b' : '#ef4444',
                          animation: isLoading ? 'pulse 1.4s ease-in-out infinite' : 'none',
                          '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isLoading ? '#f59e0b' : '#ef4444',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          fontSize: '10px',
                        }}
                      >
                        {loadingMsg}
                      </Typography>
                    </Box>
                  )
                })()}

                <TaskEventViewer event={popoverEvent} />

                <Divider sx={{ my: 2, borderColor: 'var(--form-border)' }} />
                {popoverEvent.end.getTime() > Date.now() &&
                  (() => {
                    const task = selectedEvent ? getTaskById(selectedEvent.event_id as string) : null
                    const robot = task ? robots.find(r => r.id === task.robot_id) : null
                    const isBlocked = task
                      ? getLoadingStatus(task.status) !== 'Loaded' && robot?.network_status === 'online'
                      : false
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <CustomButton.Outlined
                          className={style.button}
                          style={{
                            width: '100px',
                            opacity: isBlocked ? 0.3 : 1,
                            pointerEvents: isBlocked ? 'none' : 'auto',
                          }}
                          onClick={() => {
                            handleClosePopover()
                            const isSingle = isAllValueDisabled(popoverEvent)
                            if (isSingle) {
                              setSingleTaskEditing(true)
                              setIsSettingsOpen(true)
                              setEventScope(EventScopes.CURRENT)
                            } else {
                              setIsEditConfirmationOpen(true)
                            }
                          }}
                          disabled={isBlocked}
                        >
                          {t('common.edit')}
                        </CustomButton.Outlined>
                        <CustomButton.Outlined
                          className={style.button}
                          style={{
                            width: '100px',
                            color: '#ff4d4f',
                            borderColor: '#ff4d4f',
                            opacity: isBlocked ? 0.3 : 1,
                            pointerEvents: isBlocked ? 'none' : 'auto',
                          }}
                          onClick={() => {
                            handleClosePopover()
                            setDeleteDescription('')
                            setDeleteReason('')
                            setOpenDeleteScheduleDialog(true)
                          }}
                          disabled={isBlocked}
                        >
                          {t('common.delete')}
                        </CustomButton.Outlined>
                      </Box>
                    )
                  })()}
              </Box>
            </Box>
          )}
        </Popover>

        {isEditConfirmationOpen && (
          <ConfirmationDialogAllSingle
            confirmText={'Ok'}
            cancelText="Cancel"
            open={true}
            title={'Edit the scheduled task?'}
            submitting={undefined}
            onClose={() => {
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
              setSelectedEvent(null)
            }}
            onSubmit={() => {
              if (eventScope === EventScopes.ALL) setSingleTaskEditing(false)
              else if (eventScope === EventScopes.CURRENT) setSingleTaskEditing(true)
              else throw new Error(`Unknown event scope: ${eventScope}`)
              setIsSettingsOpen(true)
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
            }}
          >
            <EventEditDeletePopup
              currentValue={EventScopes.CURRENT}
              allValue={EventScopes.ALL}
              value={eventScope}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
            />
          </ConfirmationDialogAllSingle>
        )}

        {(() => {
          const taskForEdit = selectedEvent ? getTaskById(selectedEvent.event_id as string) : undefined
          const target: DialogTarget = taskForEdit
            ? isSingleTaskEditing
              ? { kind: 'task_occurrence', taskId: taskForEdit.id }
              : { kind: 'task_all', taskId: taskForEdit.id }
            : { kind: 'new' }
          let unshiftedDate: Date | null = null
          let tOffset: number | undefined = undefined
          if (isSingleTaskEditing && selectedEvent) {
            tOffset = selectedEvent.extendedProps?.taskOffsetMinutes
            // We must reverse the exact shift applied by filteredCalendarEvents
            const shiftBackMs = (displayOffsetMinutes - -selectedEvent.start.getTimezoneOffset()) * 60_000
            unshiftedDate = new Date(selectedEvent.start.getTime() - shiftBackMs)
          }

          return (
            <TaskSettingsModal
              isOpen={isSettingsOpen}
              onClose={onSettingsCloseClicked}
              taskName={'Cleaning Task'}
              target={target}
              preselectedData={taskForEdit ? { task: taskForEdit } : undefined}
              dateToEdit={unshiftedDate}
              initialTaskOffsetMinutes={tOffset}
              onSubmit={onSettingsSubmitClicked}
            />
          )
        })()}
        {openDeleteScheduleDialog && (
          <ConfirmationDialogAllSingle
            confirmText={isAllValueDisabled(selectedEvent) ? t('common.delete') : 'Ok'}
            cancelText="Cancel"
            open={openDeleteScheduleDialog}
            isSmall={isAllValueDisabled(selectedEvent)}
            danger={true}
            title={isAllValueDisabled(selectedEvent) ? 'Delete the task?' : 'Delete the scheduled task?'}
            disableConfirm={
              !isAllValueDisabled(selectedEvent) &&
              eventScope === EventScopes.CURRENT &&
              (!deleteReason || (deleteReason === TASK_CANCEL_REASON_OTHER && !deleteDescription))
            }
            submitting={undefined}
            onClose={() => {
              setOpenDeleteScheduleDialog(false)
              setEventScope(EventScopes.CURRENT)
              setDeleteDescription('')
              setDeleteReason('')
              setSelectedEvent(null)
            }}
            onSubmit={handleSubmitDeleteSchedule}
          >
            {!isAllValueDisabled(selectedEvent) && (
              <>
                <EventEditDeletePopup
                  currentValue={EventScopes.CURRENT}
                  allValue={EventScopes.ALL}
                  value={eventScope}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
                />
                {eventScope === EventScopes.CURRENT && (
                  <Box sx={{ marginBottom: '30px' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'var(--text-secondary)' }}>
                        Reason
                      </Typography>
                      <Dropdown
                        placeholder="Select Reason"
                        currentOption={deleteReason ? { value: deleteReason, text: deleteReason } : undefined}
                        options={[...TASK_CANCEL_REASON_OPTIONS]}
                        onClickOption={(opt: IOption) => setDeleteReason(String(opt.value))}
                      />
                    </Box>
                    {deleteReason === TASK_CANCEL_REASON_OTHER && (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Description"
                        value={deleteDescription}
                        onChange={e => setDeleteDescription(e.target.value)}
                        placeholder="Please provide a reason..."
                        required
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </ConfirmationDialogAllSingle>
        )}
      </div>
    </>
  )
}

export default TaskScheduler
